import React, { useState, useEffect, useRef } from 'react';
import { Toaster, toast } from 'react-hot-toast';
import { MdOutlineCancel, MdExpandMore, MdChevronRight } from 'react-icons/md';
import { ArrowDownTrayIcon, TrashIcon, EyeIcon } from '@heroicons/react/24/solid';
import { useStateContext } from '../../contexts/ContextProvider';
import { getCarpetaEquipo, updateCarpeta } from '../../services/Carpetas';
import { getConfigCarpetaMandante } from '../../services/CarpetaDocMandante';
import { getManDocuMaqFiltro, updateManDocuMaqFile } from '../../services/ManDocuMaq';
import { getDocumentosGenerales } from '../../services/DocumentosGenerales';
import { getEquipoAnexos, upsertEquipoAnexo, deleteEquipoAnexo } from '../../services/EquipoAnexo';
import { uploadFile, downloadFile, fetchFileUrl } from '../../services/filesTools';
import SeleccionaFecha from '../../components/DatePicker/SeleccionaFecha';

// ─── Utilidades ───────────────────────────────────────────────────────────────

const esPdf = (nombre) => nombre?.toLowerCase().endsWith('.pdf');

function sumarDias(fecha, dias) {
    if (!fecha || !dias) return null;
    const d = new Date(fecha);
    d.setDate(d.getDate() + Number(dias));
    return d;
}

function toDateInput(v) {
    if (!v) return '';
    return new Date(v).toISOString().split('T')[0];
}

function formatFecha(v) {
    if (!v) return '—';
    return new Date(v).toLocaleDateString('es-CL');
}

function diasDiferencia(fechaStr) {
    if (!fechaStr) return { texto: '', dias: 0, vencida: false };
    let fecha;
    if (fechaStr instanceof Date) {
        fecha = new Date(fechaStr);
    } else {
        const s = typeof fechaStr === 'string' && fechaStr.includes('T') ? fechaStr.split('T')[0] : fechaStr;
        if (typeof s === 'string' && s.includes('/') && s.split('/').length === 3) {
            const [d, m, y] = s.split('/');
            fecha = new Date(y, m - 1, d);
        } else if (typeof s === 'string' && s.includes('-')) {
            const [y, m, d] = s.split('-');
            fecha = new Date(Number(y), Number(m) - 1, Number(d));
        } else {
            fecha = new Date(s);
        }
    }
    if (isNaN(fecha)) return { texto: '', dias: 0, vencida: false };
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    fecha.setHours(0, 0, 0, 0);
    const diffDias = Math.round((fecha - hoy) / (1000 * 60 * 60 * 24));
    const vencida = diffDias < 0;
    const dd = String(fecha.getDate()).padStart(2, '0');
    const mm = String(fecha.getMonth() + 1).padStart(2, '0');
    const yyyy = fecha.getFullYear();
    const texto = `${dd}/${mm}/${yyyy} (${diffDias >= 0 ? diffDias : `-${Math.abs(diffDias)}`}) días`;
    return { texto, dias: diffDias, vencida };
}

function estadoDocBadge(doc) {
    if (!doc.idArchivo) return { label: 'Pendiente', cls: 'bg-yellow-100 text-yellow-700' };
    if (doc.idDocumento?.exigeFecha === 'NO') return { label: 'Entregado', cls: 'bg-green-100 text-green-700' };
    if (!doc.fechaVencimiento) return { label: 'Sin fecha', cls: 'bg-blue-100 text-blue-700' };
    const hoy = new Date();
    const vence = new Date(doc.fechaVencimiento);
    if (vence < hoy) return { label: 'Vencido', cls: 'bg-red-100 text-red-700' };
    if ((vence - hoy) / 86400000 <= 30) return { label: 'Por vencer', cls: 'bg-yellow-100 text-yellow-700' };
    return { label: 'Vigente', cls: 'bg-green-100 text-green-700' };
}

// ─── Historial ────────────────────────────────────────────────────────────────

function CarpetaHistorica({ carpeta }) {
    const [open, setOpen] = useState(false);
    const [preview, setPreview] = useState({ open: false, url: null, nombre: '' });

    const handlePrevisualizar = async (doc) => {
        if (!doc.idArchivo) return;
        const url = await fetchFileUrl(doc.idArchivo);
        setPreview({ open: true, url, nombre: doc.nombreArchivo || 'Documento' });
    };

    const handleDescargar = (doc) => {
        if (doc.idArchivo) downloadFile(doc.idArchivo, doc.nombreArchivo || 'documento.pdf');
    };

    return (
        <>
            <div className="border border-gray-200 rounded-lg mb-3 bg-white overflow-hidden">
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition"
                >
                    {open
                        ? <MdExpandMore className="text-gray-400 text-lg flex-shrink-0" />
                        : <MdChevronRight className="text-gray-400 text-lg flex-shrink-0" />}
                    <span className="text-sm font-semibold text-gray-800 flex-1">
                        {carpeta.idCentro?.nombreProyecto || carpeta.idCentro?.codigo || 'Centro desconocido'}
                    </span>
                    <span className="text-xs text-gray-400 mr-3">Carpeta N° {carpeta.numeroCarpeta}</span>
                    <span className="text-xs text-gray-400">{formatFecha(carpeta.fechaCreacion)}</span>
                    <span className="ml-2 text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">Salida de Obra</span>
                </button>

                {open && (
                    <div className="px-4 pb-4">
                        <table className="w-full border-collapse border border-gray-300 text-xs mt-2">
                            <thead>
                                <tr className="bg-gray-200 text-left">
                                    <th className="p-2 border border-gray-300 font-semibold">Documento</th>
                                    <th className="p-2 border border-gray-300 font-semibold w-32">Fecha vigencia</th>
                                    <th className="p-2 border border-gray-300 font-semibold w-32">Fecha vencimiento</th>
                                    <th className="p-2 border border-gray-300 font-semibold">Archivo</th>
                                    <th className="p-2 border border-gray-300 font-semibold w-24">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(carpeta.documentosRequeridos || []).length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="p-3 text-center text-gray-400 border border-gray-300">
                                            Sin documentos registrados.
                                        </td>
                                    </tr>
                                ) : (carpeta.documentosRequeridos || []).map((doc, i) => (
                                    <tr key={i} className="border border-gray-300 hover:bg-gray-50">
                                        <td className="p-2 border border-gray-300 bg-gray-100 font-semibold">{doc.idDocumento?.documentoGeneral || '—'}</td>
                                        <td className="p-2 border border-gray-300">{formatFecha(doc.fechaVigencia)}</td>
                                        <td className="p-2 border border-gray-300">{formatFecha(doc.fechaVencimiento)}</td>
                                        <td className="p-2 border border-gray-300 text-gray-500">{doc.nombreArchivo || '—'}</td>
                                        <td className="p-2 border border-gray-300">
                                            <div className="flex gap-2">
                                                <button
                                                    type="button"
                                                    title="Vista previa"
                                                    onClick={() => handlePrevisualizar(doc)}
                                                    disabled={!doc.idArchivo || !esPdf(doc.nombreArchivo)}
                                                    className="p-1 bg-indigo-500 text-white rounded hover:bg-indigo-600 disabled:opacity-40"
                                                >
                                                    <EyeIcon className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    type="button"
                                                    title="Descargar"
                                                    onClick={() => handleDescargar(doc)}
                                                    disabled={!doc.idArchivo}
                                                    className="p-1 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-40"
                                                >
                                                    <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {preview.open && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60"
                    onClick={() => setPreview({ open: false, url: null, nombre: '' })}
                >
                    <div
                        className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[85vh] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center px-4 py-2 border-b border-gray-200">
                            <span className="text-sm font-semibold text-gray-700">{preview.nombre}</span>
                            <button
                                onClick={() => setPreview({ open: false, url: null, nombre: '' })}
                                className="text-gray-400 hover:text-red-500 transition p-2 rounded-full hover:bg-gray-100"
                            >
                                <MdOutlineCancel className="text-xl" />
                            </button>
                        </div>
                        <iframe src={preview.url} className="flex-1 w-full rounded-b-lg" title="Preview" />
                    </div>
                </div>
            )}
        </>
    );
}

// ─── Tab Vigente ───────────────────────────────────────────────────────────────

function TabVigente({ vigente, onGuardado }) {
    const [docs, setDocs] = useState([]);
    const [baseDocs, setBaseDocs] = useState([]);
    const [guardando, setGuardando] = useState(false);
    const [preview, setPreview] = useState({ open: false, url: null, nombre: '' });

    useEffect(() => {
        const mapped = (vigente.documentosRequeridos || [])
            .filter((d) => d.idDocumento?.tipo !== 'Anexo')
            .map((d) => ({ ...d }));

        const idMandante = vigente.idCentro?.idMandante?._id;
        const idMaquina  = vigente.idMaquina?._id;

        if (!idMandante) {
            setDocs(mapped);
            setBaseDocs(mapped.map((d) => ({ ...d })));
            return;
        }

        const idsActuales = new Set(
            (vigente.documentosRequeridos || []).map((d) =>
                (d.idDocumento?._id ?? d.idDocumento)?.toString()
            )
        );

        Promise.all([
            getConfigCarpetaMandante(idMandante).catch(() => []),
            idMaquina
                ? getManDocuMaqFiltro(idMandante, idMaquina, true, false).catch(() => [])
                : Promise.resolve([]),
        ]).then(([config, manDoc]) => {
            const nuevosConfig = (Array.isArray(config) ? config : [])
                .filter((c) => c.selected && c.tipo !== 'Anexo' && !idsActuales.has(c._id?.toString()))
                .map((c) => ({
                    idDocumento: { _id: c._id, documentoGeneral: c.documentoGeneral, exigeFecha: c.exigeFecha, vigencia: c.vigencia },
                    vigenciaEfectiva: c.vigenciaPersonalizada ?? c.vigencia ?? null,
                    fechaVigencia: null, fechaVencimiento: null, nombreArchivo: null, urlArchivo: null, idArchivo: null,
                }));

            const idsNuevosConfig = new Set(nuevosConfig.map((c) => c.idDocumento._id?.toString()));

            const nuevosManDoc = (Array.isArray(manDoc) ? manDoc : [])
                .filter((d) =>
                    d.tipo !== 'Obra' &&
                    d.tipo !== 'Anexo' &&
                    !idsActuales.has(d.idDocuGeneral?.toString()) &&
                    !idsNuevosConfig.has(d.idDocuGeneral?.toString())
                )
                .map((d) => ({
                    idDocumento: { _id: d.idDocuGeneral, documentoGeneral: d.documentoGeneral, exigeFecha: d.exigeFecha, vigencia: d.vigencia },
                    vigenciaEfectiva: d.vigencia ?? null,
                    fechaVigencia: null, fechaVencimiento: null, nombreArchivo: null, urlArchivo: null, idArchivo: null,
                }));

            const todos = [...mapped, ...nuevosConfig, ...nuevosManDoc];
            setDocs(todos);
            setBaseDocs(todos.map((d) => ({ ...d })));
        });
    }, [vigente]);

    const handleFechaVigencia = (idx, date) => {
        setDocs((prev) => {
            const updated = [...prev];
            const doc = { ...updated[idx] };
            doc.fechaVigencia = date || null;
            doc.fechaVencimiento = date
                ? sumarDias(date, doc.vigenciaEfectiva ?? doc.idDocumento?.vigencia ?? 0)
                : null;
            updated[idx] = doc;
            return updated;
        });
    };

    const handleFechaVencimiento = (idx, date) => {
        setDocs((prev) => {
            const updated = [...prev];
            updated[idx] = { ...updated[idx], fechaVencimiento: date || null };
            return updated;
        });
    };

    const handleFileChange = async (idx, e) => {
        const file = e.target.files[0];
        if (!file) return;
        const tid = toast.loading('Subiendo archivo...');
        try {
            const res = await uploadFile(file);
            if (res?.fileId) {
                setDocs((prev) => {
                    const updated = [...prev];
                    updated[idx] = { ...updated[idx], nombreArchivo: file.name, urlArchivo: URL.createObjectURL(file), idArchivo: res.fileId };
                    return updated;
                });
                toast.success('Archivo cargado.', { id: tid });
            }
        } catch {
            toast.error('Error al cargar el archivo.', { id: tid });
        }
    };

    const handlePrevisualizar = async (doc) => {
        if (!doc.idArchivo) return;
        const url = await fetchFileUrl(doc.idArchivo);
        setPreview({ open: true, url, nombre: doc.nombreArchivo || 'Documento' });
    };

    const handleDescargar = (doc) => {
        if (doc.idArchivo) downloadFile(doc.idArchivo, doc.nombreArchivo || 'documento.pdf');
    };

    const handleEliminar = (idx) => {
        setDocs((prev) => {
            const updated = [...prev];
            updated[idx] = { ...updated[idx], nombreArchivo: null, urlArchivo: null, idArchivo: null };
            return updated;
        });
    };

    const cambios = docs.length !== baseDocs.length || docs.some((d, i) => {
        const b = baseDocs[i];
        if (!b) return true;
        return toDateInput(d.fechaVigencia) !== toDateInput(b.fechaVigencia)
            || d.idArchivo?.toString() !== b.idArchivo?.toString()
            || d.nombreArchivo !== b.nombreArchivo;
    });

    const handleGuardar = async () => {
        setGuardando(true);
        try {
            await updateCarpeta({
                _id: vigente._id,
                documentosRequeridos: docs.map((d) => ({
                    _id: d._id,
                    idDocumento: d.idDocumento?._id ?? d.idDocumento,
                    vigenciaEfectiva: d.vigenciaEfectiva,
                    fechaVigencia: d.fechaVigencia || null,
                    fechaVencimiento: d.fechaVencimiento || null,
                    nombreArchivo: d.nombreArchivo || null,
                    urlArchivo: d.urlArchivo || null,
                    idArchivo: d.idArchivo || null,
                })),
            });
            setBaseDocs(docs.map((d) => ({ ...d })));
            toast.success('Carpeta guardada correctamente.');
            onGuardado && onGuardado();
        } catch {
            toast.error('Error al guardar la carpeta.');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <div className="mt-5 space-y-4">
            {/* Tabla documentos */}
            <div className="bg-white rounded-lg shadow-sm p-4">
                <p className="text-sm font-bold text-gray-700 mb-3 border-b pb-2">Documentos requeridos</p>
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300 text-sm mt-2">
                        <thead>
                            <tr className="bg-gray-200 text-left">
                                <th className="text-xs font-semibold p-2 w-2/6 border border-gray-300">Documento</th>
                                <th className="text-xs font-semibold p-2 w-1/6 border border-gray-300">Estado</th>
                                <th className="text-xs font-semibold p-2 w-1/6 border border-gray-300">Fecha Emisión</th>
                                <th className="text-xs font-semibold p-2 w-1/6 border border-gray-300">Vigencia</th>
                                <th className="text-xs font-semibold p-2 w-1/6 border border-gray-300">Fecha Vencimiento</th>
                                <th className="text-xs font-semibold p-2 w-1/6 border border-gray-300">Archivo*</th>
                                <th className="text-xs font-semibold p-2 w-1/6 border border-gray-300">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {docs.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="p-4 text-center text-gray-400 border border-gray-300">
                                        Sin documentos requeridos en esta carpeta.
                                    </td>
                                </tr>
                            ) : docs.map((doc, idx) => {
                                const { label, cls } = estadoDocBadge(doc);
                                const exigeFecha = doc.idDocumento?.exigeFecha === 'SI';
                                const vig = diasDiferencia(doc.fechaVencimiento);
                                return (
                                    <tr key={idx} className="border border-gray-300 hover:bg-gray-50">
                                        {/* Documento */}
                                        <td className="border p-2 bg-gray-100 font-semibold text-xs">
                                            {doc.idDocumento?.documentoGeneral || '—'}
                                        </td>

                                        {/* Estado */}
                                        <td className="border p-2">
                                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${cls}`}>{label}</span>
                                        </td>

                                        {/* Fecha Emisión */}
                                        <td className="border p-2">
                                            {exigeFecha ? (
                                                <SeleccionaFecha
                                                    selectedDate={doc.fechaVigencia ? new Date(doc.fechaVigencia) : null}
                                                    handleDateChange={(date) => handleFechaVigencia(idx, date)}
                                                />
                                            ) : 'N/A'}
                                        </td>

                                        {/* Vigencia */}
                                        <td className={`border p-2 font-semibold text-xs ${exigeFecha && vig.vencida ? 'bg-red-300 text-red-900' : 'bg-gray-100'}`}>
                                            {exigeFecha ? (vig.texto || '—') : 'N/A'}
                                        </td>

                                        {/* Fecha Vencimiento */}
                                        <td className="border p-2">
                                            {exigeFecha ? (
                                                <SeleccionaFecha
                                                    selectedDate={doc.fechaVencimiento ? new Date(doc.fechaVencimiento) : null}
                                                    handleDateChange={(date) => handleFechaVencimiento(idx, date)}
                                                />
                                            ) : 'N/A'}
                                        </td>

                                        {/* Archivo */}
                                        <td className="border p-2">
                                            <div className="flex flex-col">
                                                <input
                                                    type="file"
                                                    accept=".pdf,.xlsx,.xls,.zip,.rar,.docx,.doc,.png,.jpg,.jpeg"
                                                    onChange={(e) => handleFileChange(idx, e)}
                                                    className="mt-1 text-xs"
                                                />
                                                {doc.nombreArchivo && (
                                                    <p className="text-xs text-gray-600 mt-1 truncate max-w-[140px]" title={doc.nombreArchivo}>
                                                        {doc.nombreArchivo}
                                                    </p>
                                                )}
                                            </div>
                                        </td>

                                        {/* Acciones */}
                                        <td className="border p-2">
                                            <div className="flex gap-2">
                                                <button
                                                    type="button"
                                                    title="Vista previa"
                                                    onClick={() => handlePrevisualizar(doc)}
                                                    disabled={!doc.idArchivo || !esPdf(doc.nombreArchivo)}
                                                    className="p-1 bg-indigo-500 text-white rounded hover:bg-indigo-600 disabled:opacity-40"
                                                >
                                                    <EyeIcon className="w-4 h-4" />
                                                </button>
                                                <button
                                                    type="button"
                                                    title="Descargar"
                                                    onClick={() => handleDescargar(doc)}
                                                    disabled={!doc.idArchivo}
                                                    className="p-1 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-40"
                                                >
                                                    <ArrowDownTrayIcon className="w-4 h-4" />
                                                </button>
                                                <button
                                                    type="button"
                                                    title="Eliminar archivo"
                                                    onClick={() => handleEliminar(idx)}
                                                    disabled={!doc.idArchivo}
                                                    className="p-1 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-40"
                                                >
                                                    <TrashIcon className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Botones */}
                <div className="flex justify-end gap-3 mt-4">
                    <button
                        onClick={() => setDocs(baseDocs.map((d) => ({ ...d })))}
                        disabled={!cambios}
                        className="text-sm px-5 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        Descartar
                    </button>
                    <button
                        onClick={handleGuardar}
                        disabled={!cambios || guardando}
                        className="text-sm px-5 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {guardando ? 'Guardando…' : 'Guardar cambios'}
                    </button>
                </div>
            </div>

            {/* Modal preview */}
            {preview.open && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60"
                    onClick={() => setPreview({ open: false, url: null, nombre: '' })}
                >
                    <div
                        className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[85vh] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center px-4 py-2 border-b border-gray-200">
                            <span className="text-sm font-semibold text-gray-700">{preview.nombre}</span>
                            <button
                                onClick={() => setPreview({ open: false, url: null, nombre: '' })}
                                className="text-gray-400 hover:text-red-500 transition p-2 rounded-full hover:bg-gray-100"
                            >
                                <MdOutlineCancel className="text-xl" />
                            </button>
                        </div>
                        <iframe src={preview.url} className="flex-1 w-full rounded-b-lg" title="Preview" />
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Tab Anexos ────────────────────────────────────────────────────────────────

function TabAnexos({ idMandante, idMaquina, codigoEquipo, documentosVigente, vigenteId, onGuardado }) {
    const [docs, setDocs]       = useState([]);
    const [baseDocs, setBaseDocs] = useState([]);
    const [guardando, setGuardando] = useState(false);
    const [preview, setPreview] = useState({ open: false, url: null, nombre: '' });

    const [docsExtra, setDocsExtra] = useState([]);
    const [baseDocsExtra, setBaseDocsExtra] = useState([]);
    const [catalogo, setCatalogo] = useState([]);
    const [requeridosIds, setRequeridosIds] = useState(new Set());
    const [nuevoDocId, setNuevoDocId] = useState('');
    const [guardandoExtra, setGuardandoExtra] = useState(false);
    const [moviendoIdx, setMoviendoIdx] = useState(null);

    useEffect(() => {
        if (!idMandante || !idMaquina) return;
        getManDocuMaqFiltro(idMandante, idMaquina, false, false, true)
            .then((lista) => {
                setDocs(lista || []);
                setBaseDocs((lista || []).map((d) => ({ ...d })));
            })
            .catch(() => {});
    }, [idMandante, idMaquina]);

    // Documentos adicionales: catálogo general no exigido por este mandante para esta máquina,
    // se guardan por equipo (no afectan la carpeta de documentos requeridos de otros equipos)
    useEffect(() => {
        if (!idMandante || !idMaquina || !codigoEquipo) return;
        Promise.all([
            getEquipoAnexos(codigoEquipo).catch(() => []),
            getManDocuMaqFiltro(idMandante, idMaquina, false, false, false).catch(() => []),
            getDocumentosGenerales().catch(() => []),
        ]).then(([anexos, requeridos, catalogoCompleto]) => {
            setDocsExtra(anexos || []);
            setBaseDocsExtra((anexos || []).map((d) => ({ ...d })));

            // "Requerido" = ya exigido vía manDocuMaq, o ya presente (aunque pendiente) en la
            // carpeta vigente — esto último cubre lo exigido por config del mandante o por el
            // fallback de "todo el catálogo", que manDocuMaq por sí solo no refleja.
            const idsRequeridos = new Set((requeridos || []).map((d) => d.idDocuGeneral?.toString()));
            (documentosVigente || []).forEach((d) => {
                const id = (d.idDocumento?._id ?? d.idDocumento)?.toString();
                if (id) idsRequeridos.add(id);
            });
            setRequeridosIds(idsRequeridos);
            setCatalogo((catalogoCompleto || []).filter((d) => !idsRequeridos.has(d._id?.toString())));
        });
    }, [idMandante, idMaquina, codigoEquipo, documentosVigente]);

    const handleFechaVigencia = (idx, date) => {
        setDocs((prev) => {
            const updated = [...prev];
            const doc = { ...updated[idx] };
            doc.fechaVigencia = date || null;
            doc.fechaVencimiento = date ? sumarDias(date, doc.vigencia ?? 0) : null;
            updated[idx] = doc;
            return updated;
        });
    };

    const handleFechaVencimiento = (idx, date) => {
        setDocs((prev) => {
            const updated = [...prev];
            updated[idx] = { ...updated[idx], fechaVencimiento: date || null };
            return updated;
        });
    };

    const handleFileChange = async (idx, e) => {
        const file = e.target.files[0];
        if (!file) return;
        const tid = toast.loading('Subiendo archivo...');
        try {
            const res = await uploadFile(file);
            if (res?.fileId) {
                setDocs((prev) => {
                    const updated = [...prev];
                    updated[idx] = { ...updated[idx], nombreArchivo: file.name, idArchivo: res.fileId };
                    return updated;
                });
                toast.success('Archivo cargado.', { id: tid });
            }
        } catch {
            toast.error('Error al cargar el archivo.', { id: tid });
        }
    };

    const handlePrevisualizar = async (doc) => {
        if (!doc.idArchivo) return;
        const url = await fetchFileUrl(doc.idArchivo);
        setPreview({ open: true, url, nombre: doc.nombreArchivo || 'Documento' });
    };

    const handleDescargar = (doc) => {
        if (doc.idArchivo) downloadFile(doc.idArchivo, doc.nombreArchivo || 'documento.pdf');
    };

    const handleEliminar = (idx) => {
        setDocs((prev) => {
            const updated = [...prev];
            updated[idx] = { ...updated[idx], nombreArchivo: null, idArchivo: null };
            return updated;
        });
    };

    const cambios = docs.some((d, i) => {
        const b = baseDocs[i];
        if (!b) return true;
        return d.idArchivo?.toString() !== b.idArchivo?.toString()
            || toDateInput(d.fechaVigencia) !== toDateInput(b.fechaVigencia);
    });

    const handleGuardar = async () => {
        setGuardando(true);
        try {
            await Promise.all(
                docs.map((d, i) => {
                    const b = baseDocs[i];
                    const changed = !b
                        || d.idArchivo?.toString() !== b.idArchivo?.toString()
                        || toDateInput(d.fechaVigencia) !== toDateInput(b.fechaVigencia);
                    if (!changed) return Promise.resolve();
                    return updateManDocuMaqFile(d._id, {
                        idArchivo: d.idArchivo || null,
                        nombreArchivo: d.nombreArchivo || null,
                        fechaVigencia: d.fechaVigencia || null,
                        fechaVencimiento: d.fechaVencimiento || null,
                    });
                })
            );
            setBaseDocs(docs.map((d) => ({ ...d })));
            toast.success('Anexos guardados correctamente.');
        } catch {
            toast.error('Error al guardar los anexos.');
        } finally {
            setGuardando(false);
        }
    };

    // ── Documentos adicionales (no exigidos por el mandante, solo para este equipo) ──

    const idsExtraActuales = new Set(docsExtra.map((d) => d.idDocumento?.toString()));
    const catalogoParaAgregar = catalogo.filter((c) => !idsExtraActuales.has(c._id?.toString()));

    const handleAgregarExtra = () => {
        if (!nuevoDocId) return;
        const doc = catalogo.find((c) => c._id === nuevoDocId);
        if (!doc) return;
        setDocsExtra((prev) => [...prev, {
            _id: null,
            idDocumento: doc._id,
            documentoGeneral: doc.documentoGeneral,
            exigeFecha: doc.exigeFecha,
            vigencia: doc.vigencia,
            idArchivo: null,
            nombreArchivo: null,
            fechaVigencia: null,
            fechaVencimiento: null,
        }]);
        setNuevoDocId('');
    };

    const handleFechaVigenciaExtra = (idx, date) => {
        setDocsExtra((prev) => {
            const updated = [...prev];
            const doc = { ...updated[idx] };
            doc.fechaVigencia = date || null;
            doc.fechaVencimiento = date ? sumarDias(date, doc.vigencia ?? 0) : null;
            updated[idx] = doc;
            return updated;
        });
    };

    const handleFechaVencimientoExtra = (idx, date) => {
        setDocsExtra((prev) => {
            const updated = [...prev];
            updated[idx] = { ...updated[idx], fechaVencimiento: date || null };
            return updated;
        });
    };

    const handleFileChangeExtra = async (idx, e) => {
        const file = e.target.files[0];
        if (!file) return;
        const tid = toast.loading('Subiendo archivo...');
        try {
            const res = await uploadFile(file);
            if (res?.fileId) {
                setDocsExtra((prev) => {
                    const updated = [...prev];
                    updated[idx] = { ...updated[idx], nombreArchivo: file.name, idArchivo: res.fileId };
                    return updated;
                });
                toast.success('Archivo cargado.', { id: tid });
            }
        } catch {
            toast.error('Error al cargar el archivo.', { id: tid });
        }
    };

    const handleMoverARequerido = async (idx) => {
        const doc = docsExtra[idx];
        if (!vigenteId) return;
        setMoviendoIdx(idx);
        try {
            const actuales = (documentosVigente || []).map((d) => ({
                _id: d._id,
                idDocumento: d.idDocumento?._id ?? d.idDocumento,
                vigenciaEfectiva: d.vigenciaEfectiva,
                fechaVigencia: d.fechaVigencia,
                fechaVencimiento: d.fechaVencimiento,
                nombreArchivo: d.nombreArchivo,
                urlArchivo: d.urlArchivo,
                idArchivo: d.idArchivo,
            }));
            const idxExistente = actuales.findIndex((d) => d.idDocumento?.toString() === doc.idDocumento?.toString());
            const entrada = {
                idDocumento: doc.idDocumento,
                vigenciaEfectiva: doc.vigencia ?? null,
                fechaVigencia: doc.fechaVigencia || null,
                fechaVencimiento: doc.fechaVencimiento || null,
                nombreArchivo: doc.nombreArchivo || null,
                urlArchivo: null,
                idArchivo: doc.idArchivo || null,
            };
            if (idxExistente >= 0) {
                actuales[idxExistente] = { ...actuales[idxExistente], ...entrada };
            } else {
                actuales.push(entrada);
            }
            await updateCarpeta({ _id: vigenteId, documentosRequeridos: actuales });
            if (doc._id) await deleteEquipoAnexo(doc._id);

            setDocsExtra((prev) => prev.filter((_, i) => i !== idx));
            setBaseDocsExtra((prev) => prev.filter((d) => d._id !== doc._id || !doc._id));
            toast.success('Documento movido a Carpeta Vigente.');
            onGuardado && onGuardado();
        } catch {
            toast.error('Error al mover el documento a Carpeta Vigente.');
        } finally {
            setMoviendoIdx(null);
        }
    };

    const handleQuitarExtra = async (idx) => {
        const doc = docsExtra[idx];
        if (doc._id) {
            try {
                await deleteEquipoAnexo(doc._id);
            } catch {
                toast.error('Error al quitar el documento.');
                return;
            }
        }
        setDocsExtra((prev) => prev.filter((_, i) => i !== idx));
        setBaseDocsExtra((prev) => prev.filter((d) => d._id !== doc._id || !doc._id));
        toast.success('Documento quitado.');
    };

    const cambiosExtra = docsExtra.length !== baseDocsExtra.length || docsExtra.some((d, i) => {
        const b = baseDocsExtra[i];
        if (!b) return true;
        return d.idArchivo?.toString() !== b.idArchivo?.toString()
            || toDateInput(d.fechaVigencia) !== toDateInput(b.fechaVigencia);
    });

    const handleGuardarExtra = async () => {
        setGuardandoExtra(true);
        try {
            const actualizados = await Promise.all(
                docsExtra.map(async (d, i) => {
                    const b = baseDocsExtra[i];
                    const changed = !b
                        || d.idArchivo?.toString() !== b.idArchivo?.toString()
                        || toDateInput(d.fechaVigencia) !== toDateInput(b.fechaVigencia);
                    if (!changed) return d;
                    const saved = await upsertEquipoAnexo({
                        _id: d._id,
                        codigoEquipo,
                        idDocumento: d.idDocumento,
                        idArchivo: d.idArchivo || null,
                        nombreArchivo: d.nombreArchivo || null,
                        fechaVigencia: d.fechaVigencia || null,
                        fechaVencimiento: d.fechaVencimiento || null,
                    });
                    return { ...d, _id: saved._id };
                })
            );
            setDocsExtra(actualizados);
            setBaseDocsExtra(actualizados.map((d) => ({ ...d })));
            toast.success('Documentos adicionales guardados.');
        } catch {
            toast.error('Error al guardar los documentos adicionales.');
        } finally {
            setGuardandoExtra(false);
        }
    };

    if (!idMandante || !idMaquina) {
        return (
            <div className="bg-white p-8 rounded-lg shadow-sm mt-5 text-center text-sm text-gray-400">
                Sin contexto de mandante para cargar anexos.
            </div>
        );
    }

    return (
        <div className="mt-5 space-y-4">
            <div className="bg-white rounded-lg shadow-sm p-4">
                <p className="text-sm font-bold text-gray-700 mb-3 border-b pb-2">Documentos Anexos</p>
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300 text-sm mt-2">
                        <thead>
                            <tr className="bg-gray-200 text-left">
                                <th className="text-xs font-semibold p-2 w-2/6 border border-gray-300">Documento</th>
                                <th className="text-xs font-semibold p-2 w-1/6 border border-gray-300">Estado</th>
                                <th className="text-xs font-semibold p-2 w-1/6 border border-gray-300">Fecha Emisión</th>
                                <th className="text-xs font-semibold p-2 w-1/6 border border-gray-300">Vigencia</th>
                                <th className="text-xs font-semibold p-2 w-1/6 border border-gray-300">Fecha Vencimiento</th>
                                <th className="text-xs font-semibold p-2 w-1/6 border border-gray-300">Archivo</th>
                                <th className="text-xs font-semibold p-2 w-1/6 border border-gray-300">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {docs.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="p-4 text-center text-gray-400 border border-gray-300">
                                        Sin documentos anexos asignados para este equipo.
                                    </td>
                                </tr>
                            ) : docs.map((doc, idx) => {
                                const fakeDoc = { idArchivo: doc.idArchivo, fechaVencimiento: doc.fechaVencimiento, idDocumento: { exigeFecha: doc.exigeFecha } };
                                const { label, cls } = estadoDocBadge(fakeDoc);
                                const exigeFecha = doc.exigeFecha === 'SI';
                                const vig = diasDiferencia(doc.fechaVencimiento);
                                return (
                                    <tr key={doc._id || idx} className="border border-gray-300 hover:bg-gray-50">
                                        <td className="border p-2 bg-gray-100 font-semibold text-xs">{doc.documentoGeneral || '—'}</td>
                                        <td className="border p-2">
                                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${cls}`}>{label}</span>
                                        </td>
                                        <td className="border p-2">
                                            {exigeFecha ? (
                                                <SeleccionaFecha
                                                    selectedDate={doc.fechaVigencia ? new Date(doc.fechaVigencia) : null}
                                                    handleDateChange={(date) => handleFechaVigencia(idx, date)}
                                                />
                                            ) : 'N/A'}
                                        </td>
                                        <td className={`border p-2 font-semibold text-xs ${exigeFecha && vig.vencida ? 'bg-red-300 text-red-900' : 'bg-gray-100'}`}>
                                            {exigeFecha ? (vig.texto || '—') : 'N/A'}
                                        </td>
                                        <td className="border p-2">
                                            {exigeFecha ? (
                                                <SeleccionaFecha
                                                    selectedDate={doc.fechaVencimiento ? new Date(doc.fechaVencimiento) : null}
                                                    handleDateChange={(date) => handleFechaVencimiento(idx, date)}
                                                />
                                            ) : 'N/A'}
                                        </td>
                                        <td className="border p-2">
                                            <div className="flex flex-col">
                                                <input type="file" accept=".pdf,.xlsx,.xls,.zip,.rar,.docx,.doc,.png,.jpg,.jpeg" onChange={(e) => handleFileChange(idx, e)} className="mt-1 text-xs" />
                                                {doc.nombreArchivo && (
                                                    <p className="text-xs text-gray-600 mt-1 truncate max-w-[140px]" title={doc.nombreArchivo}>{doc.nombreArchivo}</p>
                                                )}
                                            </div>
                                        </td>
                                        <td className="border p-2">
                                            <div className="flex gap-2">
                                                <button type="button" title="Vista previa" onClick={() => handlePrevisualizar(doc)} disabled={!doc.idArchivo || !esPdf(doc.nombreArchivo)} className="p-1 bg-indigo-500 text-white rounded hover:bg-indigo-600 disabled:opacity-40">
                                                    <EyeIcon className="w-4 h-4" />
                                                </button>
                                                <button type="button" title="Descargar" onClick={() => handleDescargar(doc)} disabled={!doc.idArchivo} className="p-1 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-40">
                                                    <ArrowDownTrayIcon className="w-4 h-4" />
                                                </button>
                                                <button type="button" title="Eliminar archivo" onClick={() => handleEliminar(idx)} disabled={!doc.idArchivo} className="p-1 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-40">
                                                    <TrashIcon className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="flex justify-end gap-3 mt-4">
                    <button onClick={() => setDocs(baseDocs.map((d) => ({ ...d })))} disabled={!cambios} className="text-sm px-5 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition disabled:opacity-40 disabled:cursor-not-allowed">
                        Descartar
                    </button>
                    <button onClick={handleGuardar} disabled={!cambios || guardando} className="text-sm px-5 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed">
                        {guardando ? 'Guardando…' : 'Guardar cambios'}
                    </button>
                </div>
            </div>

            {/* Documentos adicionales: catálogo general no exigido por este mandante, cargado solo para este equipo */}
            <div className="bg-white rounded-lg shadow-sm p-4">
                <p className="text-sm font-bold text-gray-700 mb-3 border-b pb-2">Documentos Adicionales</p>

                <div className="flex gap-2 mb-4">
                    <select
                        className="text-xs border border-gray-300 p-1.5 rounded-md flex-1"
                        value={nuevoDocId}
                        onChange={(e) => setNuevoDocId(e.target.value)}
                    >
                        <option value="">Selecciona un documento para agregar…</option>
                        {catalogoParaAgregar.map((c) => (
                            <option key={c._id} value={c._id}>{c.documentoGeneral}</option>
                        ))}
                    </select>
                    <button
                        type="button"
                        onClick={handleAgregarExtra}
                        disabled={!nuevoDocId}
                        className="text-xs px-4 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        Agregar
                    </button>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300 text-sm">
                        <thead>
                            <tr className="bg-gray-200 text-left">
                                <th className="text-xs font-semibold p-2 w-2/6 border border-gray-300">Documento</th>
                                <th className="text-xs font-semibold p-2 w-1/6 border border-gray-300">Estado</th>
                                <th className="text-xs font-semibold p-2 w-1/6 border border-gray-300">Fecha Emisión</th>
                                <th className="text-xs font-semibold p-2 w-1/6 border border-gray-300">Vigencia</th>
                                <th className="text-xs font-semibold p-2 w-1/6 border border-gray-300">Fecha Vencimiento</th>
                                <th className="text-xs font-semibold p-2 w-1/6 border border-gray-300">Archivo</th>
                                <th className="text-xs font-semibold p-2 w-1/6 border border-gray-300">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {docsExtra.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="p-4 text-center text-gray-400 border border-gray-300">
                                        Sin documentos adicionales agregados para este equipo.
                                    </td>
                                </tr>
                            ) : docsExtra.map((doc, idx) => {
                                const fakeDoc = { idArchivo: doc.idArchivo, fechaVencimiento: doc.fechaVencimiento, idDocumento: { exigeFecha: doc.exigeFecha } };
                                const { label, cls } = estadoDocBadge(fakeDoc);
                                const exigeFecha = doc.exigeFecha === 'SI';
                                const vig = diasDiferencia(doc.fechaVencimiento);
                                const yaRequerido = requeridosIds.has(doc.idDocumento?.toString());
                                return (
                                    <tr key={doc._id || `nuevo-${idx}`} className={`border border-gray-300 hover:bg-gray-50 ${yaRequerido ? 'bg-orange-50' : ''}`}>
                                        <td className="border p-2 bg-gray-100 font-semibold text-xs">
                                            {doc.documentoGeneral || '—'}
                                            {yaRequerido && (
                                                <p className="text-[10px] font-normal text-orange-600 mt-0.5">Ya es requerido en Carpeta Vigente</p>
                                            )}
                                        </td>
                                        <td className="border p-2">
                                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${cls}`}>{label}</span>
                                        </td>
                                        <td className="border p-2">
                                            {exigeFecha ? (
                                                <SeleccionaFecha
                                                    selectedDate={doc.fechaVigencia ? new Date(doc.fechaVigencia) : null}
                                                    handleDateChange={(date) => handleFechaVigenciaExtra(idx, date)}
                                                />
                                            ) : 'N/A'}
                                        </td>
                                        <td className={`border p-2 font-semibold text-xs ${exigeFecha && vig.vencida ? 'bg-red-300 text-red-900' : 'bg-gray-100'}`}>
                                            {exigeFecha ? (vig.texto || '—') : 'N/A'}
                                        </td>
                                        <td className="border p-2">
                                            {exigeFecha ? (
                                                <SeleccionaFecha
                                                    selectedDate={doc.fechaVencimiento ? new Date(doc.fechaVencimiento) : null}
                                                    handleDateChange={(date) => handleFechaVencimientoExtra(idx, date)}
                                                />
                                            ) : 'N/A'}
                                        </td>
                                        <td className="border p-2">
                                            <div className="flex flex-col">
                                                <input type="file" accept=".pdf,.xlsx,.xls,.zip,.rar,.docx,.doc,.png,.jpg,.jpeg" onChange={(e) => handleFileChangeExtra(idx, e)} className="mt-1 text-xs" />
                                                {doc.nombreArchivo && (
                                                    <p className="text-xs text-gray-600 mt-1 truncate max-w-[140px]" title={doc.nombreArchivo}>{doc.nombreArchivo}</p>
                                                )}
                                            </div>
                                        </td>
                                        <td className="border p-2">
                                            <div className="flex gap-2">
                                                <button type="button" title="Vista previa" onClick={() => handlePrevisualizar(doc)} disabled={!doc.idArchivo || !esPdf(doc.nombreArchivo)} className="p-1 bg-indigo-500 text-white rounded hover:bg-indigo-600 disabled:opacity-40">
                                                    <EyeIcon className="w-4 h-4" />
                                                </button>
                                                <button type="button" title="Descargar" onClick={() => handleDescargar(doc)} disabled={!doc.idArchivo} className="p-1 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-40">
                                                    <ArrowDownTrayIcon className="w-4 h-4" />
                                                </button>
                                                {yaRequerido && (
                                                    <button
                                                        type="button"
                                                        title="Mover a Carpeta Vigente"
                                                        onClick={() => handleMoverARequerido(idx)}
                                                        disabled={!doc.idArchivo || moviendoIdx === idx}
                                                        className="text-[10px] px-2 py-1 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-40"
                                                    >
                                                        {moviendoIdx === idx ? '...' : 'Mover a Vigente'}
                                                    </button>
                                                )}
                                                <button type="button" title="Quitar documento" onClick={() => handleQuitarExtra(idx)} className="p-1 bg-red-500 text-white rounded hover:bg-red-600">
                                                    <TrashIcon className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="flex justify-end gap-3 mt-4">
                    <button onClick={() => setDocsExtra(baseDocsExtra.map((d) => ({ ...d })))} disabled={!cambiosExtra} className="text-sm px-5 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition disabled:opacity-40 disabled:cursor-not-allowed">
                        Descartar
                    </button>
                    <button onClick={handleGuardarExtra} disabled={!cambiosExtra || guardandoExtra} className="text-sm px-5 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed">
                        {guardandoExtra ? 'Guardando…' : 'Guardar cambios'}
                    </button>
                </div>
            </div>

            {preview.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60" onClick={() => setPreview({ open: false, url: null, nombre: '' })}>
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center px-4 py-2 border-b border-gray-200">
                            <span className="text-sm font-semibold text-gray-700">{preview.nombre}</span>
                            <button onClick={() => setPreview({ open: false, url: null, nombre: '' })} className="text-gray-400 hover:text-red-500 transition p-2 rounded-full hover:bg-gray-100">
                                <MdOutlineCancel className="text-xl" />
                            </button>
                        </div>
                        <iframe src={preview.url} className="flex-1 w-full rounded-b-lg" title="Preview" />
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Componente principal (drawer de pantalla completa) ────────────────────────

const DetalleCarpeta = ({ data, onChangeDatos }) => {
    const { isClicked, setIsClicked } = useStateContext();
    const [detalleData, setDetalleData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('vigente');

    const codigoEquipo = data?.codigoEquipo;

    const cerrar = () => {
        setIsClicked({ ...isClicked, detalleFichaEquipo: false });
    };

    const cargarDatos = async () => {
        setLoading(true);
        try {
            setDetalleData(await getCarpetaEquipo(codigoEquipo));
        } catch {
            setDetalleData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (codigoEquipo) cargarDatos();
    }, [codigoEquipo]);

    const vigente  = detalleData?.vigente  || null;
    const historico = detalleData?.historico || [];

    const maquinaNombre = data?.idMaquina?.nombreMaquina || '—';
    const idMandante = vigente?.idCentro?.idMandante?._id
        || historico[0]?.idCentro?.idMandante?._id
        || null;
    const idMaquina  = data?.idMaquina?._id || null;

    const tabs = [
        { id: 'vigente',   label: 'Carpeta vigente' },
        { id: 'historial', label: `Historial (${historico.length})` },
        { id: 'anexos',    label: 'Anexos' },
    ];

    // Campos de información del equipo
    const campos = [
        { label: 'Código equipo',       value: codigoEquipo || '—' },
        { label: 'Tipo de máquina',     value: maquinaNombre },
        { label: 'Patente',             value: data?.patente || '—' },
        { label: 'Serie',               value: data?.serie   || '—' },
        { label: 'Año',                 value: data?.anno    || '—' },
    ];

    return (
        <div className="bg-half-transparent w-full fixed nav-item top-0 right-0 z-50">
            <Toaster position="top-right" />
            <div className="overflow-y-scroll float-right h-screen duration-1000 ease-in-out dark:text-gray-200 transition-all dark:bg-[#484B52] bg-gray-100 w-full md:w-full p-8">

                {/* Header */}
                <div className="flex justify-between items-center px-4 py-2 border-b border-gray-200 bg-white rounded-t-md shadow-sm">
                    <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse" />
                        <p className="text-gray-800 font-semibold text-base md:text-lg">
                            Ficha del Equipo
                            <span className="text-gray-400 font-normal mx-2">/</span>
                            <span className="font-mono">{codigoEquipo}</span>
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={cerrar}
                        className="text-gray-400 hover:text-red-500 transition-colors duration-150 p-2 rounded-full hover:bg-gray-100"
                    >
                        <MdOutlineCancel className="text-xl md:text-2xl" />
                    </button>
                </div>

                {/* Datos del equipo */}
                <div className="bg-white p-6 rounded-lg shadow-lg mt-5">
                    <div className="grid grid-cols-2 md:grid-cols-8 gap-4">
                        {campos.map(({ label, value }) => (
                            <div key={label} className="flex flex-col col-span-1">
                                <label className="text-xs font-semibold mb-1">{label}</label>
                                <input
                                    type="text"
                                    readOnly
                                    value={value}
                                    className="text-[11px] border border-gray-300 p-1.5 rounded-md bg-gray-50 text-gray-700 focus:outline-none"
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 mt-4 border-b border-gray-300">
                    {tabs.map(({ id, label }) => (
                        <button
                            key={id}
                            onClick={() => setTab(id)}
                            className={`text-xs font-semibold px-4 py-2 rounded-t border transition-colors ${
                                tab === id
                                    ? 'bg-white border-gray-300 border-b-white text-blue-600 -mb-px'
                                    : 'bg-gray-100 border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {/* Contenido */}
                {loading ? (
                    <div className="bg-white p-8 rounded-lg shadow-sm mt-5 text-center text-sm text-gray-400">
                        Cargando carpeta...
                    </div>
                ) : tab === 'vigente' ? (
                    vigente ? (
                        <TabVigente
                            vigente={vigente}
                            onGuardado={() => {
                                cargarDatos();
                                onChangeDatos && onChangeDatos('REFRESH');
                            }}
                        />
                    ) : (
                        <div className="bg-white p-8 rounded-lg shadow-sm mt-5 text-center text-sm text-gray-400">
                            Este equipo no tiene carpeta vigente actualmente.
                        </div>
                    )
                ) : tab === 'historial' ? (
                    <div className="mt-5">
                        {historico.length === 0 ? (
                            <div className="bg-white p-8 rounded-lg shadow-sm text-center text-sm text-gray-400">
                                Sin carpetas históricas para este equipo.
                            </div>
                        ) : historico.map((c) => (
                            <CarpetaHistorica key={c._id} carpeta={c} />
                        ))}
                    </div>
                ) : (
                    <TabAnexos
                        idMandante={idMandante}
                        idMaquina={idMaquina}
                        codigoEquipo={codigoEquipo}
                        documentosVigente={vigente?.documentosRequeridos}
                        vigenteId={vigente?._id}
                        onGuardado={() => {
                            cargarDatos();
                            onChangeDatos && onChangeDatos('REFRESH');
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default DetalleCarpeta;
